// Add/edit a parts line.
// CRITICAL: When `ui_jobPartsSection` is OFF, the job picker is hidden AND
// `job_id` is forced to null in the submit handler — even when editing a row
// that previously had a job assignment.

import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useFeatureFlag, useExposureFlag } from '@/hooks/useFeatureFlags';
import { PartsPricingHint } from './PartsPricingHint';
import { PartsStatusSelect } from './PartsStatusSelect';
import { InventoryPickerPopover } from '@/components/inventory/InventoryPickerPopover';
import { useInventoryItemById } from '@/hooks/inventory/useInventoryItemById';
import { useReserveInventoryForPartsLine } from '@/hooks/inventory/useReserveInventoryForPartsLine';
import { useReleaseInventoryReservation } from '@/hooks/inventory/useReleaseInventoryReservation';
import { StockPill } from '@/components/inventory/StockPill';
import { Boxes } from 'lucide-react';
import type { InventoryItem } from '@/hooks/inventory/useInventoryItems';
import type { PartsLine, PartsLineStatus, PartsLineInsert } from '@/hooks/parts/usePartsLines';

export interface JobOption {
  job_id: string;
  name: string;
  /** Phase 5C — optional book minutes; consumed by intake modal job-context line. */
  standardMinutes?: number;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shopId: string;
  ticketId: string;
  jobOptions: JobOption[];
  initial?: PartsLine | null;
  onSubmit: (payload: PartsLineInsert | (Partial<PartsLine> & { id: string })) => Promise<void> | void;
}

const emptyForm = {
  vendor_name: '',
  vendor_part_number: '',
  description: '',
  category: '',
  quantity: '1',
  unit_cost: '',
  unit_sell: '',
  taxable: true,
  status: 'needed' as PartsLineStatus,
  availability_text: '',
  eta_text: '',
  core_charge: '0',
  notes: '',
  job_id: '__ticket__' as string,
  inventory_item_id: null as string | null,
};

export function PartsLineFormDialog({
  open,
  onOpenChange,
  shopId,
  ticketId,
  jobOptions,
  initial,
  onSubmit,
}: Props) {
  const jobSectionEnabled = useExposureFlag('ui_jobPartsSection');
  const inventoryEnabled = useFeatureFlag('inventoryEnabled');
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const reserveMut = useReserveInventoryForPartsLine();
  const releaseMut = useReleaseInventoryReservation();

  useEffect(() => {
    if (open) {
      if (initial) {
        setForm({
          vendor_name: initial.vendor_name ?? '',
          vendor_part_number: initial.vendor_part_number ?? '',
          description: initial.description,
          category: initial.category ?? '',
          quantity: String(initial.quantity ?? 1),
          unit_cost: initial.unit_cost == null ? '' : String(initial.unit_cost),
          unit_sell: initial.unit_sell == null ? '' : String(initial.unit_sell),
          taxable: initial.taxable,
          status: initial.status,
          availability_text: initial.availability_text ?? '',
          eta_text: initial.eta_text ?? '',
          core_charge: String(initial.core_charge ?? 0),
          notes: initial.notes ?? '',
          job_id: initial.job_id ?? '__ticket__',
          inventory_item_id: initial.inventory_item_id ?? null,
        });
      } else {
        setForm(emptyForm);
      }
    }
  }, [open, initial]);

  // Linked inventory context — feeds the in-form linked card and the picker.
  const linkedItemQ = useInventoryItemById(inventoryEnabled ? form.inventory_item_id : null);
  const linkedItem = linkedItemQ.data ?? null;
  const currentReserved = Number(initial?.inventory_reservation_qty ?? 0);
  const currentConsumed = Number(initial?.inventory_consumed_qty ?? 0);
  // Phase 9 R4 — Block unlink/relink while either reservation OR consumed > 0.
  // Consumed stock is owed back to inventory; clearing the link would orphan it.
  const linkLocked = currentReserved > 0 || currentConsumed > 0;
  const linkLockedReason = linkLocked
    ? currentConsumed > 0
      ? `Return consumed stock (${currentConsumed}) before changing or clearing the linked item.`
      : `Release the current reservation (${currentReserved}) before changing or clearing the linked item.`
    : undefined;

  const handlePickInventory = (item: InventoryItem) => {
    // Manual override supremacy: only fill empty fields. Never overwrite populated values.
    setForm((prev) => ({
      ...prev,
      inventory_item_id: item.id,
      vendor_part_number: prev.vendor_part_number || item.supplier_part_number || item.part_number || '',
      description: prev.description || item.name || '',
      category: prev.category || item.category || '',
      vendor_name: prev.vendor_name || item.supplier || '',
      unit_cost:
        prev.unit_cost === '' && item.unit_cost != null
          ? String(item.unit_cost)
          : prev.unit_cost,
      unit_sell:
        prev.unit_sell === '' && item.unit_sell_default != null
          ? String(item.unit_sell_default)
          : prev.unit_sell,
    }));
  };

  const handleClearInventory = () => {
    setForm((prev) => ({ ...prev, inventory_item_id: null }));
  };

  const handleUseDefaults = () => {
    if (!linkedItem) return;
    setForm((prev) => ({
      ...prev,
      vendor_part_number: prev.vendor_part_number || linkedItem.supplier_part_number || linkedItem.part_number || '',
      description: prev.description || linkedItem.name || '',
      category: prev.category || linkedItem.category || '',
      vendor_name: prev.vendor_name || linkedItem.supplier || '',
      unit_cost:
        prev.unit_cost === '' && linkedItem.unit_cost != null
          ? String(linkedItem.unit_cost)
          : prev.unit_cost,
      unit_sell:
        prev.unit_sell === '' && linkedItem.unit_sell_default != null
          ? String(linkedItem.unit_sell_default)
          : prev.unit_sell,
    }));
  };

  const previouslyJobLinked = !!initial?.job_id;
  const willDetachJob = !jobSectionEnabled && previouslyJobLinked;

  const handleSubmit = async () => {
    if (!form.description.trim()) return;
    setSubmitting(true);
    try {
      // CRITICAL: When job-section UI is disabled, force job_id = null.
      const resolvedJobId = jobSectionEnabled
        ? (form.job_id === '__ticket__' ? null : form.job_id)
        : null;

      const sellRaw = form.unit_sell === '' ? null : Number(form.unit_sell);
      const wasLocked = !!initial?.manual_price_locked;
      // DMC-I: actual_sell_each is the sole per-unit authority for the
      // "did the user edit sell?" comparison. The legacy `initial.unit_sell`
      // mirror fallback was retired after DMC-H — Phase 3.5 enforce-authority
      // trigger guarantees actual_sell_each is populated for any persisted row.
      const previousEach = initial?.actual_sell_each ?? null;
      const userEditedSell = sellRaw !== null && (
        !initial || Number(sellRaw) !== Number(previousEach ?? NaN)
      );
      // Lock when:
      //  - editing an already-locked line and not clearing the price, OR
      //  - the user touched the sell field with a value.
      const manual_price_locked = sellRaw != null && (wasLocked || userEditedSell);

      // Inventory linkage. The RPC-only fields (`inventory_reservation_qty`,
      // `inventory_consumed_qty`) are deliberately NEVER included in this payload.
      const base: any = {
        shop_id: shopId,
        ticket_id: ticketId,
        job_id: resolvedJobId,
        vendor_name: form.vendor_name.trim() || null,
        vendor_part_number: form.vendor_part_number.trim() || null,
        description: form.description.trim(),
        category: form.category.trim() || null,
        quantity: Number(form.quantity || 1),
        unit_cost: form.unit_cost === '' ? null : Number(form.unit_cost),
        // Authority-contract fields. Per-unit authority on form-driven entry.
        actual_sell_each: sellRaw,
        actual_sell_total: null, // trigger derives from each (per-unit authority)
        pricing_source: sellRaw != null ? 'manual' : (initial?.pricing_source ?? 'manual'),
        pricing_method: sellRaw != null ? 'manual_entry' : (initial?.pricing_method ?? 'manual_entry'),
        manual_price_locked,
        taxable: form.taxable,
        status: form.status,
        availability_text: form.availability_text.trim() || null,
        eta_text: form.eta_text.trim() || null,
        core_charge: Number(form.core_charge || 0),
        source_type: 'manual',
        source_reference: null,
        notes: form.notes.trim() || null,
        // Phase 9 R3 — Inventory linkage (form-writable). Only included when
        // entitlement is on so we don't accidentally null out existing links.
        ...(inventoryEnabled ? { inventory_item_id: form.inventory_item_id } : {}),
      };

      if (initial) {
        await onSubmit({ id: initial.id, ...base });
      } else {
        await onSubmit(base);
      }
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{initial ? 'Edit Part' : 'Add Part'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {inventoryEnabled && (
            <div className="rounded-md border bg-muted/30 p-3 space-y-2">
              <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                <Boxes className="h-3.5 w-3.5" /> Inventory
              </div>
              <InventoryPickerPopover
                value={form.inventory_item_id}
                onPick={handlePickInventory}
                onClear={handleClearInventory}
                locked={linkLocked}
                lockedReason={linkLockedReason}
              />
              {linkedItem && (
                <>
                  <div className="rounded-md border bg-background p-2 text-xs space-y-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium tabular-nums">{linkedItem.part_number}</span>
                      <StockPill
                        available={linkedItem.quantity_available ?? 0}
                        reorderPoint={linkedItem.reorder_point ?? 0}
                      />
                    </div>
                    <div className="text-muted-foreground truncate">{linkedItem.name}</div>
                    <div className="text-muted-foreground tabular-nums">
                      {linkedItem.bin_location && <>Bin {linkedItem.bin_location} · </>}
                      On-hand {linkedItem.quantity_on_hand ?? 0} ·
                      Reserved {linkedItem.quantity_reserved ?? 0} ·
                      Available {linkedItem.quantity_available ?? 0}
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    className="h-auto p-0 text-xs"
                    onClick={handleUseDefaults}
                  >
                    Use inventory defaults (fills empty fields only)
                  </Button>
                </>
              )}
              {initial && form.inventory_item_id && (
                <div className="rounded-md border bg-background p-2 space-y-2">
                  <div className="text-xs font-medium">Reservation</div>
                  {currentReserved > 0 ? (
                    <div className="flex items-center justify-between gap-2 text-xs">
                      <span className="tabular-nums">
                        Currently reserved: <span className="font-semibold">{currentReserved}</span>
                      </span>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-7"
                        disabled={releaseMut.isPending}
                        onClick={() =>
                          releaseMut.mutate({
                            partsLineId: initial.id,
                            ticketId: initial.ticket_id,
                            inventoryItemId: form.inventory_item_id!,
                          })
                        }
                      >
                        Release
                      </Button>
                    </div>
                  ) : (
                    <ReserveInline
                      defaultQty={Number(form.quantity || 1)}
                      maxQty={(linkedItem?.quantity_available ?? 0) + currentReserved}
                      disabled={reserveMut.isPending}
                      onReserve={(qty) =>
                        reserveMut.mutate({
                          partsLineId: initial.id,
                          qty,
                          ticketId: initial.ticket_id,
                          inventoryItemId: form.inventory_item_id!,
                        })
                      }
                    />
                  )}
                </div>
              )}
              {!initial && form.inventory_item_id && (
                <p className="text-[11px] text-muted-foreground">
                  Save the part, then reserve from the row or by editing it.
                </p>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="vendor_name">Vendor</Label>
              <Input
                id="vendor_name"
                value={form.vendor_name}
                onChange={(e) => setForm({ ...form, vendor_name: e.target.value })}
                placeholder="e.g. WORLDPAC"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="vendor_part_number">Vendor Part #</Label>
              <Input
                id="vendor_part_number"
                value={form.vendor_part_number}
                onChange={(e) => setForm({ ...form, vendor_part_number: e.target.value })}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="description">Description *</Label>
            <Input
              id="description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Front brake pads, premium ceramic"
              required
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="category">Category</Label>
              <Input
                id="category"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                placeholder="brakes"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="quantity">Qty</Label>
              <Input
                id="quantity"
                type="number"
                min="0"
                step="0.5"
                value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="core_charge">Core charge</Label>
              <Input
                id="core_charge"
                type="number"
                min="0"
                step="0.01"
                value={form.core_charge}
                onChange={(e) => setForm({ ...form, core_charge: e.target.value })}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="unit_cost">Unit cost</Label>
              <Input
                id="unit_cost"
                type="number"
                min="0"
                step="0.01"
                value={form.unit_cost}
                onChange={(e) => setForm({ ...form, unit_cost: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="unit_sell">Unit sell (override)</Label>
              <Input
                id="unit_sell"
                type="number"
                min="0"
                step="0.01"
                value={form.unit_sell}
                onChange={(e) => setForm({ ...form, unit_sell: e.target.value })}
                placeholder="leave blank to use suggested"
              />
            </div>
          </div>

          <PartsPricingHint
            qty={Number(form.quantity || 1)}
            unitCost={form.unit_cost === '' ? null : Number(form.unit_cost)}
            unitSell={form.unit_sell === '' ? null : Number(form.unit_sell)}
            category={form.category.trim() || null}
          />

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="availability_text">Availability</Label>
              <Input
                id="availability_text"
                value={form.availability_text}
                onChange={(e) => setForm({ ...form, availability_text: e.target.value })}
                placeholder="In stock, 2 hubs"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="eta_text">ETA</Label>
              <Input
                id="eta_text"
                value={form.eta_text}
                onChange={(e) => setForm({ ...form, eta_text: e.target.value })}
                placeholder="Tomorrow AM"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Status</Label>
              <PartsStatusSelect
                value={form.status}
                onChange={(next) => setForm({ ...form, status: next })}
              />
            </div>
            <div className="flex items-center gap-3 pt-6">
              <Switch
                id="taxable"
                checked={form.taxable}
                onCheckedChange={(v) => setForm({ ...form, taxable: !!v })}
              />
              <Label htmlFor="taxable">Taxable</Label>
            </div>
          </div>

          {/* Job picker — gated by ui_jobPartsSection. When OFF, picker is hidden
              AND submit forces job_id=null (defense in depth in handleSubmit). */}
          {jobSectionEnabled && (
            <div className="space-y-1.5">
              <Label>Assign to job</Label>
              <Select
                value={form.job_id}
                onValueChange={(v) => setForm({ ...form, job_id: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__ticket__">Whole ticket</SelectItem>
                  {jobOptions.map((j) => (
                    <SelectItem key={j.job_id} value={j.job_id}>
                      {j.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {willDetachJob && (
            <div className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning-foreground">
              Job-level parts UI is disabled — saving will detach this part from its job.
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="notes">Notes (internal)</Label>
            <Textarea
              id="notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || !form.description.trim()}>
            {initial ? 'Save changes' : 'Add part'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Inline reserve helper (edit dialog only) ─────────────────────────────
function ReserveInline({
  defaultQty,
  maxQty,
  disabled,
  onReserve,
}: {
  defaultQty: number;
  maxQty: number;
  disabled?: boolean;
  onReserve: (qty: number) => void;
}) {
  const [qty, setQty] = useState<string>(String(Math.max(0, defaultQty)));
  const n = Number(qty);
  const valid = Number.isFinite(n) && n > 0 && n <= maxQty;
  return (
    <div className="flex items-center gap-2">
      <Input
        type="number"
        min="0"
        step="1"
        value={qty}
        onChange={(e) => setQty(e.target.value)}
        className="h-7 w-24"
      />
      <span className="text-[11px] text-muted-foreground tabular-nums">
        max {maxQty}
      </span>
      <Button
        type="button"
        size="sm"
        className="h-7 ml-auto"
        disabled={disabled || !valid}
        onClick={() => valid && onReserve(n)}
      >
        Reserve
      </Button>
    </div>
  );
}
